import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Plus, ArrowUp, ArrowDown, Check, X, Tag } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { notifyCategoriesDirty } from "@/context/CategoriesContext";

const SWATCHES = [
  "#FFD700", "#32D74B", "#FF3B30", "#7CDBFF", "#A78BFA", "#FF9F0A",
  "#5AC8FA", "#BF5AF2", "#FFFFFF", "#FF6B9D", "#34C759", "#8E8E93",
];

function LoadingRow() {
  return (
    <div className="flex items-center gap-2 text-tw-muted font-mono text-xs py-6 justify-center">
      <Loader2 className="animate-spin" size={14} /> LADEN…
    </div>
  );
}

export function CategoriesAdmin() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [editing, setEditing] = useState({}); // id -> {description, color}
  const [renaming, setRenaming] = useState({}); // id -> draft name
  const [showNew, setShowNew] = useState(false);
  const [newCat, setNewCat] = useState({ name: "", description: "", color: SWATCHES[0] });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/categories");
      setCats(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createCategory = async () => {
    if (!newCat.name.trim()) return;
    setCreating(true);
    try {
      await api.post("/admin/categories", newCat);
      toast.success(`Categorie "${newCat.name.trim()}" aangemaakt.`);
      setNewCat({ name: "", description: "", color: SWATCHES[0] });
      setShowNew(false);
      notifyCategoriesDirty();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const saveEdit = async (c) => {
    const draft = editing[c.id];
    if (!draft) return;
    setBusyId(c.id);
    try {
      await api.patch(`/admin/categories/${c.id}`, { description: draft.description, color: draft.color });
      toast.success("Categorie bijgewerkt.");
      setEditing((p) => { const n = { ...p }; delete n[c.id]; return n; });
      notifyCategoriesDirty();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const saveRename = async (c) => {
    const newName = (renaming[c.id] || "").trim();
    if (!newName || newName === c.name) {
      setRenaming((p) => { const n = { ...p }; delete n[c.id]; return n; });
      return;
    }
    if (!window.confirm(`Naam wijzigen? ${c.memo_count} memo's en ${c.question_count} vragen worden bijgewerkt.`)) return;
    setBusyId(c.id);
    try {
      await api.patch(`/admin/categories/${c.id}`, { name: newName });
      toast.success(`Categorie hernoemd naar "${newName}".`);
      setRenaming((p) => { const n = { ...p }; delete n[c.id]; return n; });
      notifyCategoriesDirty();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const toggleActive = async (c) => {
    if (c.active) {
      // About to deactivate — warn if it's currently reader-visible, since existing memos
      // in it stay visible to lezers until the switch in Bibliotheek-instellingen is off too.
      try {
        const { data } = await api.get("/admin/library-settings");
        if ((data.reader_visible_categories || []).includes(c.name)) {
          const ok = window.confirm(
            `Categorie "${c.name}" deactiveren?\n\nLet op: deze categorie staat aan voor lezers — bestaande memo's blijven voor lezers zichtbaar totdat je de schakelaar in Bibliotheek-instellingen uitzet.`
          );
          if (!ok) return;
        }
      } catch {
        // fail-soft: if the check itself fails, still allow the deactivate to proceed
      }
    }
    setBusyId(c.id);
    try {
      await api.patch(`/admin/categories/${c.id}`, { active: !c.active });
      notifyCategoriesDirty();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const [reordering, setReordering] = useState(false);

  const reorder = async (index, dir) => {
    if (reordering) return;
    const swapIdx = index + dir;
    if (swapIdx < 0 || swapIdx >= cats.length) return;
    const next = [...cats];
    [next[index], next[swapIdx]] = [next[swapIdx], next[index]];
    // Optimistic renumbering so the on-screen order numbers update immediately, before the
    // server round-trip / reload confirms it.
    const renumbered = next.map((c, i) => ({ ...c, sort_order: i }));
    setCats(renumbered);
    setReordering(true);
    try {
      await api.post("/admin/categories/reorder", { ids: renumbered.map((c) => c.id) });
      notifyCategoriesDirty();
    } catch (e) {
      toast.error(formatApiError(e));
      await load();
    } finally {
      setReordering(false);
    }
  };

  const remove = async (c) => {
    if (c.memo_count > 0 || c.question_count > 0) return;
    if (!window.confirm(`Categorie "${c.name}" definitief verwijderen?`)) return;
    setBusyId(c.id);
    try {
      await api.delete(`/admin/categories/${c.id}`);
      toast.success(`Categorie "${c.name}" verwijderd.`);
      notifyCategoriesDirty();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="admin-categories-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Tag size={14} className="text-tw-yellow" strokeWidth={2.5} />
          <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted">CATEGORIEËN</div>
        </div>
        <Button
          onClick={() => setShowNew((v) => !v)}
          data-testid="admin-category-new-toggle"
          className="h-9 rounded-none bg-tw-yellow text-black hover:bg-[#E6C200] uppercase tracking-[0.2em] text-[10px] font-bold"
        >
          <Plus size={14} className="mr-1.5" /> Nieuwe categorie
        </Button>
      </div>

      {showNew && (
        <div className="bg-tw-surface border border-tw p-4 mb-4 space-y-3" data-testid="admin-category-new-form">
          <Input
            value={newCat.name}
            onChange={(e) => setNewCat((p) => ({ ...p, name: e.target.value }))}
            placeholder="Naam (2-30 tekens)"
            data-testid="admin-category-new-name"
            className="bg-tw-black border-tw text-white h-10 rounded-none focus-visible:ring-0 focus:border-tw-yellow"
          />
          <Textarea
            value={newCat.description}
            onChange={(e) => setNewCat((p) => ({ ...p, description: e.target.value }))}
            placeholder="Korte Nederlandse uitleg (max 300 tekens)"
            rows={2}
            maxLength={300}
            data-testid="admin-category-new-description"
            className="bg-tw-black border-tw text-white rounded-none focus-visible:ring-0 focus:border-tw-yellow resize-none"
          />
          <div className="flex flex-wrap gap-2">
            {SWATCHES.map((sw) => (
              <button
                key={sw}
                type="button"
                onClick={() => setNewCat((p) => ({ ...p, color: sw }))}
                data-testid={`admin-category-new-swatch-${sw.replace("#", "")}`}
                className={`w-6 h-6 border-2 ${newCat.color === sw ? "border-tw-yellow" : "border-transparent"}`}
                style={{ backgroundColor: sw }}
                aria-label={sw}
              />
            ))}
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              onClick={() => setShowNew(false)}
              variant="outline"
              className="h-9 rounded-none bg-transparent border-tw text-white hover:bg-tw-surface-hover hover:text-white uppercase tracking-[0.2em] text-[10px]"
            >
              Annuleer
            </Button>
            <Button
              onClick={createCategory}
              disabled={creating || !newCat.name.trim()}
              data-testid="admin-category-new-save"
              className="h-9 rounded-none bg-tw-yellow text-black hover:bg-[#E6C200] uppercase tracking-[0.2em] text-[10px] font-bold disabled:opacity-50"
            >
              {creating ? <Loader2 className="animate-spin" size={14} /> : "Opslaan"}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <LoadingRow />
      ) : (
        <div className="space-y-2" data-testid="admin-categories-list">
          {cats.map((c, i) => {
            const draft = editing[c.id];
            const isEditing = !!draft;
            const isRenaming = renaming[c.id] !== undefined;
            const canDelete = c.memo_count === 0 && c.question_count === 0;
            return (
              <article
                key={c.id}
                data-testid={`admin-category-${c.name}`}
                className={`bg-tw-surface border p-4 ${c.active ? "border-tw" : "border-tw opacity-60"}`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex flex-col items-center gap-1 pt-1 shrink-0">
                    <button
                      onClick={() => reorder(i, -1)}
                      disabled={i === 0 || reordering}
                      data-testid={`admin-category-up-${c.name}`}
                      className="p-0.5 text-tw-muted hover:text-tw-yellow transition-colors disabled:opacity-30"
                      aria-label="Omhoog"
                    >
                      <ArrowUp size={14} strokeWidth={2.5} />
                    </button>
                    <span className="font-mono text-[10px] text-tw-muted">{c.sort_order}</span>
                    <button
                      onClick={() => reorder(i, 1)}
                      disabled={i === cats.length - 1 || reordering}
                      data-testid={`admin-category-down-${c.name}`}
                      className="p-0.5 text-tw-muted hover:text-tw-yellow transition-colors disabled:opacity-30"
                      aria-label="Omlaag"
                    >
                      <ArrowDown size={14} strokeWidth={2.5} />
                    </button>
                  </div>

                  <span className="inline-block w-3 h-3 mt-1.5 shrink-0" style={{ backgroundColor: c.color }} aria-hidden />

                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      {isRenaming ? (
                        <>
                          <Input
                            value={renaming[c.id]}
                            onChange={(e) => setRenaming((p) => ({ ...p, [c.id]: e.target.value }))}
                            data-testid={`admin-category-rename-input-${c.name}`}
                            className="bg-tw-black border-tw text-white h-8 rounded-none focus-visible:ring-0 focus:border-tw-yellow w-48"
                          />
                          <button onClick={() => saveRename(c)} data-testid={`admin-category-rename-save-${c.name}`} className="text-tw-yellow hover:text-white">
                            <Check size={14} strokeWidth={3} />
                          </button>
                          <button onClick={() => setRenaming((p) => { const n = { ...p }; delete n[c.id]; return n; })} className="text-tw-muted hover:text-white">
                            <X size={14} />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => setRenaming((p) => ({ ...p, [c.id]: c.name }))}
                          data-testid={`admin-category-name-${c.name}`}
                          className="text-white text-sm font-bold uppercase tracking-wider hover:text-tw-yellow transition-colors"
                        >
                          {c.name}
                        </button>
                      )}
                      {!c.active && (
                        <span className="font-mono text-[9px] uppercase tracking-widest text-tw-muted border border-tw px-1.5 py-0.5">(inactief)</span>
                      )}
                      <span className="font-mono text-[10px] text-tw-muted">{c.memo_count} memo's · {c.question_count} vragen</span>
                    </div>

                    {isEditing ? (
                      <div className="space-y-2">
                        <Textarea
                          value={draft.description}
                          onChange={(e) => setEditing((p) => ({ ...p, [c.id]: { ...p[c.id], description: e.target.value } }))}
                          rows={2}
                          maxLength={300}
                          data-testid={`admin-category-edit-description-${c.name}`}
                          className="bg-tw-black border-tw text-white rounded-none focus-visible:ring-0 focus:border-tw-yellow resize-none"
                        />
                        <div className="flex flex-wrap gap-2">
                          {SWATCHES.map((sw) => (
                            <button
                              key={sw}
                              type="button"
                              onClick={() => setEditing((p) => ({ ...p, [c.id]: { ...p[c.id], color: sw } }))}
                              className={`w-5 h-5 border-2 ${draft.color === sw ? "border-tw-yellow" : "border-transparent"}`}
                              style={{ backgroundColor: sw }}
                              aria-label={sw}
                            />
                          ))}
                        </div>
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => setEditing((p) => { const n = { ...p }; delete n[c.id]; return n; })}
                            className="text-[10px] uppercase tracking-widest text-tw-muted hover:text-white px-2 py-1 border border-tw"
                          >
                            Annuleer
                          </button>
                          <button
                            onClick={() => saveEdit(c)}
                            disabled={busyId === c.id}
                            data-testid={`admin-category-save-${c.name}`}
                            className="text-[10px] uppercase tracking-widest text-black bg-tw-yellow hover:bg-[#E6C200] font-bold px-3 py-1 flex items-center gap-1"
                          >
                            {busyId === c.id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} strokeWidth={3} />} Opslaan
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setEditing((p) => ({ ...p, [c.id]: { description: c.description, color: c.color } }))}
                        data-testid={`admin-category-description-${c.name}`}
                        className="text-left text-xs text-tw-muted hover:text-white transition-colors"
                      >
                        {c.description || <em className="opacity-60">Geen beschrijving — klik om toe te voegen</em>}
                      </button>
                    )}
                  </div>

                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <button
                      onClick={() => toggleActive(c)}
                      disabled={busyId === c.id}
                      data-testid={`admin-category-active-toggle-${c.name}`}
                      aria-pressed={c.active}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${c.active ? "bg-tw-yellow" : "bg-tw"}`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-black transition-transform ${c.active ? "translate-x-4" : "translate-x-1"}`} />
                    </button>
                    <button
                      onClick={() => remove(c)}
                      disabled={!canDelete || busyId === c.id}
                      data-testid={`admin-category-delete-${c.name}`}
                      title={canDelete ? "Verwijderen" : "Deactiveer in plaats van verwijderen — nog in gebruik"}
                      className="p-1 text-tw-muted hover:text-tw-red transition-colors disabled:opacity-30 disabled:hover:text-tw-muted"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
